import { Flex, LoadingIcon, Message } from "basis";
import { useAccounts } from "library/accounts";
import { getBalance, getUserProduct } from "library/api";
import { usePageTracking } from "react-event-tracker";
import { useProducts } from "library/hooks/useProducts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePaymentInstruments } from "library/hooks/usePaymentInstruments";
import { getIsEligibleForDebitCard } from "components/WaysToPay/PaymentMethods/DebitCard/getIsEligilbeForDebitCard";
import { InfoBlock } from "../InfoBlock/InfoBlock";
import { Layout } from "../Layout/Layout";
import { ProductSelector } from "../ProductSelector/ProductSelector";
import { PaymentAmounts } from "./PaymentAmounts/PaymentAmounts";
import { PaymentMethods } from "./PaymentMethods/PaymentMethods";
import styles from "./ways-to-pay.module.scss";
import { WaysToPayFAQ } from "./WaysToPayFAQ";

function scrollToId(id) {
  const yOffset = -20;
  const element = document.getElementById(id);

  if (element) {
    const y =
      element.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: "smooth" });
  }
}

export const WaysToPay = (props) => {
  usePageTracking({
    page: {
      pageType: "config-page",
      siteSection: "accounts",
      siteSubSection: "payment",
      pageTitle: "select-amount",
    },
    user: {
      authenticationStatus: "logged in",
    },
  });
  const navigate = useNavigate();
  const pageState = props.location?.state || {};
  const [commonData, setCommonData] = useState(
    props.location?.state?.paymentInfo ?? null,
  );
  const [product, setProduct] = useState({});
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [availableMethods, setAvailableMethods] = useState([]);
  const productsInfo = useProducts();
  const accountsInfo = useAccounts({ fullCreditCardsInfo: true });
  const accounts = accountsInfo.data ?? {};
  const [loadingNewProduct, setLoadingNewProduct] = useState(false);
  const [prfi, setPrfi] = useState({
    value: 0,
    enabled: false,
  });
  const [paymentAmountState, setPaymentAmountState] = useState({
    amount:
      props.location.state && props.location.state.amount
        ? props.location.state.amount
        : 0,
    selectedOption:
      props.location.state && props.location.state.selectedOption
        ? props.location.state.selectedOption
        : "",
  });
  const updatePaymentAmountState = useCallback(
    (newState) =>
      setPaymentAmountState({
        ...paymentAmountState,
        ...newState,
      }),
    [paymentAmountState],
  );
  const [customAmountState, setCustomAmountState] = useState({
    amount:
      paymentAmountState.selectedOption === "customAmount"
        ? paymentAmountState.amount
        : "",
    amountError: null,
  });
  const updateCustomAmountState = (newState) => {
    setCustomAmountState({
      ...customAmountState,
      ...newState,
    });
    updatePaymentAmountState({
      amount: newState.amount || 0,
    });
  };
  const customAmountDetails = {
    ...customAmountState,
    updateCustomAmountState,
  };
  const setProducts = useCallback((data) => {
    const acceptedMethods = [
      "direct_transfer",
      "bpay",
      "internet_banking",
      "direct_debit",
      "auto_payment",
      "by_mail",
      "in_person",
      "debit_card",
    ];
    setProduct(data);
    setAvailableMethods(
      acceptedMethods.filter((item) =>
        Object.keys(data.payment_methods).includes(item),
      ),
    );
  }, []);
  const methodsEnabled =
    commonData &&
    commonData.current_balance > 0 &&
    !customAmountDetails.amountError &&
    (paymentAmountState.amount > 0 || product.region === "NZ");

  useEffect(() => {
    if (accountsInfo.error?.response?.status === 401) {
      navigate("/login");
    }
  }, [accountsInfo, navigate]);

  useEffect(() => {
    const getAccountPrfi = async (accountId) => {
      try {
        const { data } = await getBalance(accountId);

        return data;
      } catch (err) {
        if (err.response && err.response.status === 401) {
          navigate("/login");
        }
      }

      return null;
    };
    const getProductDetails = async () => {
      if (commonData) {
        try {
          setLoadingNewProduct(true);
          const { data } = await getUserProduct(commonData.productId);
          setProducts(data);

          const accountPrfi = await getAccountPrfi(commonData.accountId);

          setPrfi({
            value: accountPrfi.prfi,
            enabled:
              data.features && data.features.prfi && data.features.prfi.enabled,
          });

          setLoadingNewProduct(false);
        } catch (err) {
          if (err.response && err.response.status === 401) {
            navigate("/login");
          }
        }
      }
    };
    getProductDetails();
  }, [commonData, setProducts, navigate]);

  useEffect(() => {
    if (product.region !== "NZ") {
      // timeout is needed because of the modalcheck and the scroll to methods checking before the dom being updated
      setTimeout(() => {
        if (methodsEnabled && !overlayOpen) {
          scrollToId("methodSelectLabel");
        } else if (
          product.id &&
          commonData &&
          !loadingNewProduct &&
          !overlayOpen &&
          paymentAmountState.selectedOption !== "customAmount"
        ) {
          scrollToId("amountSelectLabel");
        }
      }, 50);
    }
  }, [
    methodsEnabled,
    product,
    commonData,
    paymentAmountState,
    updatePaymentAmountState,
    loadingNewProduct,
    overlayOpen,
  ]);

  const updateSelectedProduct = async (productId, accountId) => {
    // clear the location state to set the original BSB and AccountNumber from CommonData
    navigate("/ways-to-pay", { replace: true });

    // reset selected option
    updatePaymentAmountState({
      amount: 0,
      selectedOption: null,
    });

    const currentProduct = productsInfo.data.find((p) => p.id === productId);
    const account =
      accounts.credit_cards &&
      accounts.credit_cards.find((a) => a.account_id === accountId);

    setLoadingNewProduct(true);

    setCommonData(null);

    setPaymentAmountState({
      amount: 0,
      selectedOption: "",
    });

    setCustomAmountState({
      amount: "",
      amountError: null,
    });

    setAvailableMethods([]);

    try {
      const { data } = await getBalance(accountId);
      setPrfi({
        value: data.prfi,
        enabled:
          currentProduct.features &&
          currentProduct.features.prfi &&
          currentProduct.features.prfi.enabled,
      });
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate("/login");
      }
    }

    setCommonData({
      accountNumber: account.account_number,
      accountId: account.account_id,
      productId: account.product_id,
      minimum_monthly_repayment: account.credit_card.minimum_monthly_repayment,
      current_balance: account.credit_card.current_balance,
      amount_immediate_due: account.credit_card.amount_immediate_due,
      payment_due_date: account.credit_card.payment_due_date,
      payment_due: account.credit_card.payment_due,
      last_statement: account.credit_card.last_statement,
      lastFour: account.credit_card.linked_card_last4,
      payinEligible: account.payin && account.payin.status === "ELIGIBLE",
      accountNum: account.payin && account.payin.bank_account,
      bsb: account.payin && account.payin.bsb,
      creditLimit: account.credit_card.credit_limit,
    });

    setProducts(currentProduct);

    setLoadingNewProduct(false);
  };

  const { data: paymentInstrumentData } = usePaymentInstruments();

  const account = useMemo(() => {
    const accountList = accountsInfo.data;
    const accountId = commonData?.accountId;
    return accountList?.credit_cards?.find(
      (acc) => acc.account_id === accountId,
    );
  }, [commonData, accountsInfo.data]);

  useEffect(() => {
    const hasSavedCards = paymentInstrumentData?.data.data.length > 0;

    const isEligibleForDebitCardPayment =
      !account || getIsEligibleForDebitCard(account);

    const isDebitCardEnabled = availableMethods.some(
      (method) => method === "debit_card",
    );

    if (
      isDebitCardEnabled &&
      !isEligibleForDebitCardPayment &&
      !hasSavedCards
    ) {
      setAvailableMethods(
        availableMethods.filter((method) => method !== "debit_card"),
      );
    }
  }, [account, availableMethods, paymentInstrumentData]);

  const availableCreditCards =
    accounts.credit_cards &&
    accounts.credit_cards.filter((acct) => acct.links.length !== 0).length;

  let messageContent;

  if (
    availableCreditCards === 0 &&
    accounts.personal_loans &&
    accounts.personal_loans.length === 0
  ) {
    messageContent = (
      <InfoBlock iconName="no-transactions" trackId="no-active-accounts">
        You do not have any active accounts to pay.
      </InfoBlock>
    );
  } else if (
    availableCreditCards === 0 &&
    accounts.personal_loans &&
    accounts.personal_loans.length > 0
  ) {
    messageContent = (
      <InfoBlock iconName="no-transactions" trackId="has-personal-loans">
        You cannot make online payments to your personal loan at this time.
      </InfoBlock>
    );
  }

  return (
    <Layout>
      <Layout.Header>Make a payment</Layout.Header>
      <Layout.Main>
        {accountsInfo.isLoading || productsInfo.isLoading ? (
          <Flex height="200" placeItems="center">
            <LoadingIcon size="medium" />
          </Flex>
        ) : (
          <>
            {pageState.pageError && (
              <Message
                severity="blocking"
                bg="secondary.pink.t30"
                testId="error-message"
              >
                {pageState.pageError}
              </Message>
            )}
            <div className={styles.row}>
              {availableCreditCards === 0 ? (
                <>{messageContent}</>
              ) : (
                <>
                  <div className={styles.labels}>1. To account</div>
                  <div className={styles.info}>
                    <ProductSelector
                      accountList={
                        accounts.credit_cards.filter(
                          (acc) => acc.status !== "CLOSED",
                        ) || []
                      }
                      onChange={updateSelectedProduct}
                      paymentInfo={commonData}
                      products={productsInfo.data}
                    />
                  </div>
                </>
              )}
            </div>
            <div
              className={`${styles.row} ${
                !product.id || loadingNewProduct ? styles.rowInactive : ""
              } `}
            >
              <div
                id="amountSelectLabel"
                className={`${styles.labels} ${
                  !product.id || loadingNewProduct ? styles.inactive : ""
                }`}
              >
                2. Choose amount
              </div>
              {loadingNewProduct ? (
                <Flex height="200" placeItems="center">
                  <LoadingIcon size="medium" />
                </Flex>
              ) : (
                product.id &&
                commonData &&
                !loadingNewProduct && (
                  <div className={`${styles.info} ${styles.amountWrapper}`}>
                    {commonData.current_balance <= 0 ? (
                      <div className={styles.amounts}>
                        <span className={styles.cardAmountHeader}>
                          Your account balance is zero or in credit.
                        </span>
                        <br />
                        You're up to date! No payment required.
                      </div>
                    ) : (
                      <PaymentAmounts
                        onSelectPaymentAmount={updatePaymentAmountState}
                        selectedOption={paymentAmountState.selectedOption}
                        setOverlayOpen={setOverlayOpen}
                        {...commonData}
                        prfi={prfi}
                        region={product.region}
                        productId={product.id}
                        customAmountDetails={customAmountDetails}
                      />
                    )}
                  </div>
                )
              )}
            </div>
            <div
              className={`${styles.row} ${
                methodsEnabled ? styles.noBorder : styles.rowInactive
              }`}
            >
              <div
                id="methodSelectLabel"
                className={`${styles.labels} ${
                  !methodsEnabled || loadingNewProduct ? styles.inactive : ""
                }`}
              >
                3. Payment method
              </div>
              {methodsEnabled && !loadingNewProduct && (
                <div className={styles.info}>
                  <PaymentMethods
                    productName={product.name}
                    product={product}
                    availableMethods={availableMethods}
                    account={account}
                    commonData={commonData}
                    amount={paymentAmountState.amount}
                    selectedOption={paymentAmountState.selectedOption}
                    customAmountDetails={customAmountDetails}
                    pageError={pageState.pageError}
                    fields={{
                      bsb: pageState.bsb || commonData.bsb,
                      accountNum: pageState.accountNum || commonData.accountNum,
                    }}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </Layout.Main>
      <Layout.Sidebar>
        <WaysToPayFAQ />
      </Layout.Sidebar>
    </Layout>
  );
};
